import { validate as isUUID } from 'uuid';

import {
  type ExecutionLogFilters,
  type ExecutionLogStatus,
  type ExecutionLogTriggerSource,
} from '~/data/dataSubscription';

import { currentLocale } from '~/utils';

type ChipColor = 'default' | 'error' | 'info' | 'success' | 'warning';

const STATUS_COLOR: Record<ExecutionLogStatus, ChipColor> = {
  failed: 'error',
  partial: 'warning',
  pending: 'info',
  success: 'success',
};

const TRIGGER_SOURCE_COLOR: Record<ExecutionLogTriggerSource, ChipColor> = {
  billed: 'default',
  manual: 'info',
  process_state_change: 'default',
  rerun: 'default',
};

/**
 * Display helpers (MUI chip colors) for execution-log status and trigger source.
 * Human-readable labels are localised in the components via i18n.
 */
export const ExecutionLogObject = {
  getStatusColor(status: ExecutionLogStatus): ChipColor {
    return STATUS_COLOR[status];
  },

  getTriggerSourceColor(source: ExecutionLogTriggerSource | null): ChipColor {
    if (!source) {
      return 'default';
    }
    return TRIGGER_SOURCE_COLOR[source];
  },

  isRerunnable(status: ExecutionLogStatus): boolean {
    return status === 'failed' || status === 'partial';
  },
};

/**
 * Splits the single execution-log search box into the two filters the API offers.
 *
 * Support pastes either a delivery note id or a delivery note number, so the term
 * decides the filter: a UUID goes to `dlnId`, everything else to `dlnNr` (which the
 * backend matches case-insensitively as a substring).
 *
 * @param searchTerm - Raw content of the search box.
 * @returns The DLN filters to merge into the execution-log query.
 */
export const toExecutionLogDlnFilters = (
  searchTerm: string | undefined,
): Pick<ExecutionLogFilters, 'dlnId' | 'dlnNr'> => {
  const term = searchTerm?.trim();

  if (!term) {
    return { dlnId: undefined, dlnNr: undefined };
  }

  return isUUID(term)
    ? { dlnId: term, dlnNr: undefined }
    : { dlnId: undefined, dlnNr: term };
};

const dateTimeFormat = new Intl.DateTimeFormat(currentLocale(), {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const dateTimeWithSecondsFormat = new Intl.DateTimeFormat(currentLocale(), {
  dateStyle: 'medium',
  timeStyle: 'medium',
});

/**
 * Formats an execution-log timestamp (ISO string or `Date`) in the current
 * locale via the Intl API. Returns an en dash for missing or unparsable dates.
 */
export const formatExecutionLogDate = (
  isoDate: Date | string | null | undefined,
  { withSeconds = false }: { withSeconds?: boolean } = {},
): string => {
  if (!isoDate) {
    return '–';
  }

  const date = new Date(isoDate);

  if (Number.isNaN(date.getTime())) {
    return '–';
  }

  return (withSeconds ? dateTimeWithSecondsFormat : dateTimeFormat).format(
    date,
  );
};
